import { Link } from "react-router-dom";
import { Hexagon, Palette, ArrowRight, Check, Handshake } from "lucide-react";

const MODES = [
  {
    key: "standalone",
    badge: "Mode 1",
    title: "O.N.E.Tech Standalone Solution",
    icon: Hexagon,
    points: [
      "Full O.N.E.Tech branding and product identity",
      "Separate secure operational intelligence portal",
      "Connects to the broker's CRM, KYC, payment, trading and communication systems",
      "Reads operational signals; writes decisions, tasks and outcomes back through simulated APIs",
      "Suitable for brokers purchasing O.N.E.Tech directly",
    ],
    cta: { label: "You are viewing this mode", to: "/", testId: "mode-standalone-cta" },
    testId: "mode-standalone-card",
  },
  {
    key: "whitelabel",
    badge: "Mode 2",
    title: "Embedded / White-Label CRM Module",
    icon: Palette,
    points: [
      "The same intelligence capabilities inside a neutral partner-CRM interface",
      "Partner name, logo and primary colour are fully configurable",
      "O.N.E.Tech technology operates behind the interface",
      "Intelligence cards, client prioritisation, next-best-action, approvals, escalations and outcome attribution rendered inside the CRM",
      "Default configuration: “Partner CRM” — no third-party names or logos are used",
    ],
    cta: { label: "Configure & preview", to: "/partnership/whitelabel", testId: "mode-whitelabel-cta" },
    testId: "mode-whitelabel-card",
  },
];

export default function Partnership() {
  return (
    <div className="space-y-6" data-testid="partnership-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100 flex items-center gap-3">
          <Handshake className="w-7 h-7 text-emerald-400" strokeWidth={1.5} /> Deployment &amp; Partnership Preview
        </h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
          Two demonstration modes for CRM technology providers evaluating O.N.E.Tech. Both run on the same synthetic
          environment and the same intelligence engine — only the presentation and ownership model differ.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {MODES.map((m) => (
          <div key={m.key} data-testid={m.testId} className="bg-zinc-900 border border-zinc-800 rounded-md p-6 flex flex-col gap-5 hover:border-zinc-700 transition-colors duration-200">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono tracking-widest text-emerald-500 uppercase">{m.badge}</span>
              <div className="w-10 h-10 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <m.icon className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
              </div>
            </div>
            <h2 className="text-xl font-bold tracking-tight text-zinc-100">{m.title}</h2>
            <div className="space-y-2.5 flex-1">
              {m.points.map((p, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-zinc-300 leading-relaxed">
                  <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> {p}
                </div>
              ))}
            </div>
            {m.key === "standalone" ? (
              <span
                data-testid={m.cta.testId}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-xs font-semibold bg-zinc-800 text-emerald-400 border border-emerald-500/40"
              >
                {m.cta.label} — this portal is Mode 1
              </span>
            ) : (
              <Link
                to={m.cta.to}
                data-testid={m.cta.testId}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-zinc-950 transition-colors duration-200"
              >
                {m.cta.label} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Link>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: "Side-by-side comparison", to: "/partnership/comparison", testId: "partnership-link-comparison" },
          { label: "Integration architecture", to: "/partnership/architecture", testId: "partnership-link-architecture" },
          { label: "Partner evaluation checklist", to: "/partnership/evaluation", testId: "partnership-link-evaluation" },
        ].map((l) => (
          <Link
            key={l.to}
            to={l.to}
            data-testid={l.testId}
            className="bg-zinc-900 border border-zinc-800 rounded-md p-5 flex items-center justify-between text-sm font-semibold text-zinc-200 hover:border-emerald-500/40 hover:text-emerald-400 transition-colors duration-200"
          >
            {l.label} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        ))}
      </div>

      <div className="border border-amber-500/20 bg-amber-500/5 rounded-sm px-4 py-3 text-[11px] font-mono text-amber-500/90" data-testid="partnership-disclaimer">
        Illustrative preview only — no pricing, no legal commitments, no named third-party providers, and every connection shown is simulated for demonstration.
      </div>
    </div>
  );
}
